import React from 'react';
import { useState, useRef } from 'react';
import { Upload, X, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  uploadFile,
  validateFile,
  FILE_TYPE_PRESETS,
  formatFileSize,
} from '../../utils/fileUpload';
import { motion, AnimatePresence } from 'framer-motion';
export function FileUpload({
  onUpload,
  onClear,
  accept,
  maxSize,
  allowedTypes,
  label = 'Upload File',
  currentFile,
  preset,
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const fileInputRef = useRef(null);
  const presetConfig = preset ? FILE_TYPE_PRESETS[preset] : undefined;
  const finalAllowedTypes = allowedTypes || presetConfig?.allowedTypes || [];
  // Allow preset/prop to intentionally disable size validation by passing null/undefined.
  const finalMaxSize = maxSize ?? presetConfig?.maxSize ?? 10 * 1024 * 1024;
  const handleChosenFile = async (file) => {
    setError(null);
    setUploading(true);
    setUploadProgress(0);
    setRetryAttempt(0);
    const validation = validateFile(file, {
      maxSize: finalMaxSize,
      allowedTypes: finalAllowedTypes,
    });
    if (!validation.valid) {
      setError(validation.error || 'Invalid file');
      setUploading(false);
      return;
    }
    try {
      const result = await uploadFile(file, {
        onProgress: setUploadProgress,
        onRetry: (attempt) => setRetryAttempt(attempt + 1),
      });
      setUploadedFile(result);
      onUpload(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      setRetryAttempt(0);
    }
  };
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await handleChosenFile(file);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!uploading) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (uploading) return;
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    await handleChosenFile(file);
  };
  const handleRemove = () => {
    setUploadedFile(null);
    setError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClear?.();
  };
  const getAcceptString = () => {
    if (accept) return accept;
    if (preset === 'image') return 'image/*';
    if (preset === 'pdf') return 'application/pdf';
    if (preset === 'video') return 'video/*';
    if (preset === 'audio') return 'audio/*';
    return '*/*';
  };
  return (
    <div className="space-y-3">
      {label && <label className="block text-sm font-bold text-slate-700">{label}</label>}
      <div
        className="relative"
        onDragOver={handleDragOver}
        onDragEnter={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={getAcceptString()}
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        <AnimatePresence mode="wait">
          {uploadedFile || currentFile ? (
            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
              className={`flex items-center gap-3 p-4 rounded-xl ${uploadedFile ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}
            >
              <CheckCircle2
                className={`w-5 h-5 flex-shrink-0 ${uploadedFile ? 'text-green-600' : 'text-slate-500'}`}
              />
              <div className="flex-1 min-w-0">
                <p
                  className={`text-sm font-bold truncate ${uploadedFile ? 'text-green-900' : 'text-slate-800'}`}
                >
                  {uploadedFile?.fileName || 'File selected'}
                </p>
                {uploadedFile && (
                  <p className="text-xs text-green-700">{formatFileSize(uploadedFile.fileSize)}</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleRemove}
                className="p-1 text-green-600 hover:text-green-800 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          ) : (
            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
              }}
            >
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className={`w-full flex items-center justify-center gap-3 px-6 py-4 border-2 border-dashed rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed ${isDragging ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-indigo-400 hover:bg-indigo-50'}`}
              >
                {uploading ? (
                  <div className="w-full space-y-2">
                    <div className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                      <span className="text-sm font-bold text-slate-700">
                        {retryAttempt > 0
                          ? `Retrying upload (${retryAttempt})...`
                          : uploadProgress > 0
                            ? `Uploading... ${uploadProgress}%`
                            : 'Starting upload...'}
                      </span>
                    </div>
                    {uploadProgress > 0 && (
                      <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200">
                        <div
                          className="h-full rounded-full bg-indigo-600 transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    )}
                    <p className="text-center text-xs text-slate-500">
                      Large files may take several minutes on slow networks. Keep this tab open.
                    </p>
                  </div>
                ) : (
                  <React.Fragment>
                    <Upload className="w-5 h-5 text-slate-400" />
                    <span className="text-sm font-bold text-slate-700">
                      {isDragging ? 'Drop file to upload' : 'Click to upload or drag and drop'}
                    </span>
                  </React.Fragment>
                )}
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
      {error && (
        <motion.div
          initial={{
            opacity: 0,
            y: -10,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl"
        >
          <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0" />
          <p className="text-sm text-rose-900">{error}</p>
        </motion.div>
      )}
      {presetConfig && presetConfig.maxSize > 0 && (
        <p className="text-xs text-slate-500">
          {presetConfig.allowedTypes.length > 0
            ? `Accepted: ${presetConfig.allowedTypes.join(', ')} · `
            : ''}
          {`Max file size: ${(presetConfig.maxSize / (1024 * 1024)).toFixed(0)} MB`}
        </p>
      )}
    </div>
  );
}
